import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Card, CardContent, Link, Typography } from "@mui/material";
import { FunctionComponent, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import AppLogo from "@/assets/images/favicon.png";
import { AuthError } from "@/components";
import { TokenPurpose } from "@/enum";

import { ROUTE_CONFIG } from "@/routes/config";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { InputField } from "@/components/InputField/InputField";

import { ChangePasswordSchema, ChangePasswordType } from "./schema/set-password";
import { useChangePasswordWithTokenMutation } from "../../redux/auth.api";

export const ChangePassword: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`basics.${key}`);

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const initialValues: ChangePasswordType = {
		password: "",
		confirmPassword: "",
	};

	const [changePassword, { isLoading, error }] = useChangePasswordWithTokenMutation();
	const {
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<ChangePasswordType>({
		defaultValues: initialValues,
		resolver: zodResolver(ChangePasswordSchema),
	});

	const [currentFormState, setCurrentFormState] =
		useState<Partial<ChangePasswordType>>(initialValues);

	const handleSaveFormState = (
		value: ChangePasswordType[keyof ChangePasswordType],
		name: keyof ChangePasswordType
	) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const formErrors = Object.values(errors).map((error) => error.message) as ERROR_TYPE[];

	const onSubmit = async (values: ChangePasswordType) => {
		try {
			await changePassword({
				token: searchParams.get("token") || "",
				purpose: TokenPurpose.FORGOTTEN_PASSWORD,
				password: values.password,
				confirmPassword: values.confirmPassword,
			}).unwrap();
			navigate(ROUTE_CONFIG.LOGIN);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<div className="w-full flex flex-col items-center">
			<div className="w-full z-10 flex flex-col justify-center items-center mb-8">
				<img alt="logo" className="w-20 h-20" src={AppLogo} />
				<div className="mb-2 text-md">{ts("setPassword")}</div>
			</div>
			<Card
				elevation={16}
				sx={{
					mb: 4,
					position: "relative",
					width: "100%",
					maxWidth: "444px",
				}}
			>
				<CardContent>
					<Box>
						<Typography mb={4} variant="h6">
							{ts("setPassword")}
						</Typography>

						<Box component="form" onSubmit={handleSubmit(onSubmit)}>
							<InputField
								error={!!errors.password?.message}
								handleChange={(event) => handleSaveFormState(event.target.value, "password")}
								name="password"
								placeholder={t("basics.password")}
								type="password"
								value={currentFormState.password || ""}
							/>

							<InputField
								error={!!errors.confirmPassword?.message}
								handleChange={(event) => handleSaveFormState(event.target.value, "confirmPassword")}
								name="confirmPassword"
								placeholder={t("basics.confirmPassword")}
								type="password"
								value={currentFormState.confirmPassword || ""}
							/>

							<Button
								isLoading={isLoading}
								size={ButtonSize.L}
								title={"Reset password"}
								type="submit"
							/>

							<Link
								component="a"
								sx={{ mt: 2, textAlign: "center", cursor: "pointer" }}
								underline="hover"
								variant="subtitle2"
								onClick={() => navigate(ROUTE_CONFIG.LOGIN)}
							>
								Back to login
							</Link>
						</Box>

						{getAllErrors(error, formErrors).length ? (
							<AuthError>{renderErrorMessages(getAllErrors(error, formErrors))}</AuthError>
						) : null}
					</Box>
				</CardContent>
			</Card>
		</div>
	);
};
